import path from 'path';
import {
  Customer,
  CustomerSigner,
  DocumentSignatureStatus,
  DocumentType,
  ProjectType,
  SettingKey,
  SupplierCategory,
  UserType,
  VATRate,
} from '@prisma/client';
import { Decimal } from 'decimal.js';

export const label = (
  key:
    | UserType
    | ProjectType
    | DocumentType
    | SettingKey
    | CustomerSigner
    | DocumentSignatureStatus
    | SupplierCategory,
) => {
  switch (key) {
    case UserType.ADMIN:
      return 'Administrateur';
    case UserType.TECHNICIAN:
      return 'Technicien';
    case UserType.SELLER:
      return 'Vendeur';
    case UserType.INSTALLER:
      return 'Poseur';
    case ProjectType.KITCHEN:
      return 'Cuisine';
    case ProjectType.COMPLEMENT:
      return 'Complément';
    case ProjectType.AFTER_SALES:
      return 'SAV';
    case ProjectType.ACCESSORY:
      return 'Accessoire';
    case DocumentType.QUOTE:
      return 'Marché';
    case DocumentType.TECHNICAL_PLAN:
      return 'Plan technique';
    case DocumentType.INITIAL_DEPOSIT_INVOICE:
      return "Facture d'acompte initial";
    case DocumentType.ON_DELIVERY_DEPOSIT_INVOICE:
      return "Facture d'acompte à la livraison";
    case DocumentType.FINAL_INVOICE:
      return 'Facture finale';
    case SettingKey.SUPPLIERS:
      return 'Fournisseurs';
    case SettingKey.ITEM_NAMES:
      return 'Désignations';
    case SettingKey.BULTHAUP_LINES:
      return 'Lignes Bulthaup';
    case SettingKey.BULTHAUP_PROGRAMS:
      return 'Programmes Bulthaup';
    case SettingKey.BULTHAUP_COLORS:
      return 'Coloris Bulthaup';
    case SettingKey.WORKTOP:
      return 'Plan de travail';
    case SettingKey.SPLASHBACK:
      return 'Crédence';
    case SettingKey.CUSTOMER_TITLES:
      return 'Civilités';
    case CustomerSigner.CLIENT1:
      return 'Client 1';
    case CustomerSigner.CLIENT2:
      return 'Client 2';
    case DocumentSignatureStatus.DONE:
      return 'Signé';
    case DocumentSignatureStatus.ONGOING:
      return 'En attente de signature';
    case DocumentSignatureStatus.DECLINED:
      return 'Décliné';
    case DocumentSignatureStatus.REJECTED:
      return 'Rejeté';
    case DocumentSignatureStatus.CANCELED:
      return 'Annulé';
    case DocumentSignatureStatus.EXPIRED:
      return 'Expiré';
    case DocumentSignatureStatus.DRAFT:
      return 'En préparation';
    case SupplierCategory.BULTHAUP:
      return 'Bulthaup';
    case SupplierCategory.SANITARY:
      return 'Sanitaires';
    case SupplierCategory.APPLIANCE:
      return 'Électroménager';
    case SupplierCategory.FURNITURE:
      return 'Plan de travail';
    case SupplierCategory.CARRIER:
      return 'Transporteur';
    case SupplierCategory.INSTALLER:
      return 'Installateur';
    case SupplierCategory.MEUBLE:
      return 'Meuble';
    case SupplierCategory.DECORATION:
      return 'Décoration';
    case SupplierCategory.WORKS:
      return 'Travaux';
    default:
      return key;
  }
};

export const getTemplatePath = (templateName: string): string => {
  const isDev = process.env.NODE_ENV === 'development';
  const basePath = isDev
    ? path.resolve(process.cwd(), 'assets', 'templates') // dev: /app/assets/templates/
    : path.resolve(process.cwd(), 'dist', 'assets', 'templates'); // prod: /app/dist/assets/templates/

  return path.join(basePath, templateName);
};
export const getFontPath = (fontName: string): string => {
  const isDev = process.env.NODE_ENV === 'development';
  const basePath = isDev
    ? path.resolve(process.cwd(), 'assets', 'fonts') // dev: /app/assets/fonts/
    : path.resolve(process.cwd(), 'dist', 'assets', 'fonts'); // prod: /app/dist/assets/fonts/

  return path.join(basePath, fontName);
};

export const getVatRate = (vatRate: VATRate | null) => {
  switch (vatRate) {
    case VATRate.TWENTY_PERCENT:
      return 0.2;
    case VATRate.TEN_PERCENT:
      return 0.1;
    case VATRate.EIGHT_PERCENT:
      return 0.08;
    default:
      return 0;
  }
};

export const discount = (basePrice: Decimal, discountPercentage: Decimal) =>
  basePrice
    .mul(new Decimal(1).sub(discountPercentage.div(100)))
    .toDecimalPlaces(2);

export const sum = (values: Decimal[]) =>
  values.reduce((acc, val) => acc.add(val), new Decimal(0));

// Split shop name if longer than 17 chars
// @generated-code
export const splitShopName = (
  name: string,
  maxLength: number = 17,
): string[] => {
  if (name.length <= maxLength) return [name];

  const words = name.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxLength) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);

  return lines;
};
